import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"
import { useMemo, useCallback } from "react"
import { toast } from "react-toastify"
import {
  Form,
  FormFeedback,
  Label,
  Button,
  Row,
  Col,
  Card,
  CardBody,
  Input,
} from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import { useQuery } from "@tanstack/react-query"
import { getUsers } from "api/userApi"
import {
  useCreateAccount,
  useEditAccount,
} from "hooks/useAccountEntity"
import { useAccountAccountTypes } from "hooks/useAccountAccountType"

const AccountAddEdit = ({ modal, toggleModal, isEdit, editedData, onSuccess }) => {
  const { mutateAsync: createMutation, isPending: createLoading } =
    useCreateAccount()
  const { mutateAsync: editMutation, isPending: editLoading } =
    useEditAccount()

  const { data: typeResponse, isLoading: typeLoading } = useAccountAccountTypes(
    { all: true, per_page: 200 },
    modal
  )

  const accountTypeOptionsRaw = typeResponse?.data?.data || typeResponse?.data || []
  const accountTypeOptions = useMemo(
    () =>
      Array.isArray(accountTypeOptionsRaw)
        ? accountTypeOptionsRaw.map(item => ({ value: item.id, label: item.name, slug: item.slug }))
        : [],
    [accountTypeOptionsRaw]
  )

  const { data: userResponse, isLoading: userLoading } = useQuery({
    queryKey: ["users", "account-entity"],
    queryFn: () => getUsers({ all: true, per_page: 200 }),
    enabled: modal,
  })

  const users = userResponse?.data?.data || userResponse?.data || []
  const userOptions = useMemo(
    () => (Array.isArray(users) ? users.map(item => ({ value: item.id, label: item.name })) : []),
    [users]
  )

  const validationSchema = Yup.object({
    name: Yup.string().trim().required("Please enter account name"),
    account_type_id: Yup.number()
      .typeError("Please select account type")
      .required("Please select account type"),
    custodian_user_id: Yup.mixed().nullable(),
    opening_balance: Yup.number()
      .typeError("Opening balance must be a number")
      .integer("Opening balance must be an integer")
      .min(0, "Opening balance cannot be negative")
      .required("Opening balance is required"),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      const payload = {
        name: values.name?.trim(),
        account_type_id: Number(values.account_type_id),
        custodian_user_id: values.custodian_user_id
          ? Number(values.custodian_user_id)
          : null,
        opening_balance: Number(values.opening_balance || 0),
      }

      const action = isEdit
        ? editMutation({ id: editedData.id, ...payload })
        : createMutation(payload)

      try {
        const res = await action
        toast.success(
          res?.message ?? `Account ${isEdit ? "updated" : "created"} successfully.`
        )
        resetForm()
        onSuccess?.(res?.data)
        toggleModal()
      } catch (error) {
        toast.error("Failed to save account.")
      }
    },
    [isEdit, editedData, editMutation, createMutation, toggleModal, onSuccess]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: editedData?.name || "",
      account_type_id:
        editedData?.account_type_id ||
        editedData?.accountType?.id ||
        editedData?.account_type?.id ||
        "",
      custodian_user_id:
        editedData?.custodian_user_id || editedData?.custodian?.id || "",
      opening_balance: editedData?.opening_balance ?? 0,
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  const selectedAccountType = accountTypeOptions.find(
    item => String(item.value) === String(formik.values.account_type_id)
  )
  const isCashAccount = selectedAccountType?.slug === "cash"

  const buttonText = useMemo(() => {
    const loading = editLoading || createLoading
    return `${isEdit ? "Save Changes" : "Add Account"}${loading ? "..." : ""}`
  }, [isEdit, editLoading, createLoading])

  const isSubmitDisabled = useMemo(
    () => editLoading || createLoading || typeLoading,
    [editLoading, createLoading, typeLoading]
  )

  return (
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title={isEdit ? "Edit Account" : "Add Account"}
      size="lg"
    >
      <Form
        onSubmit={e => {
          e.preventDefault()
          formik.handleSubmit()
        }}
      >
        <Card className="border">
          <CardBody>
            <h5 className="mb-3">Basic Information</h5>
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <Label htmlFor="name">Account Name</Label>
                  <InputField
                    type="text"
                    name="name"
                    id="name"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    invalid={formik.touched.name && !!formik.errors.name}
                  />
                  {formik.touched.name && formik.errors.name && (
                    <FormFeedback type="invalid">{formik.errors.name}</FormFeedback>
                  )}
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <Label htmlFor="account_type_id">Account Type</Label>
                  <Input
                    type="select"
                    name="account_type_id"
                    id="account_type_id"
                    value={formik.values.account_type_id}
                    onChange={formik.handleChange}
                    invalid={
                      formik.touched.account_type_id && !!formik.errors.account_type_id
                    }
                    disabled={typeLoading}
                  >
                    <option value="">Select account type</option>
                    {accountTypeOptions.map(item => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </Input>
                  {formik.touched.account_type_id && formik.errors.account_type_id && (
                    <FormFeedback type="invalid">
                      {formik.errors.account_type_id}
                    </FormFeedback>
                  )}
                </div>
              </Col>
            </Row>
            {isCashAccount && (
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <Label htmlFor="custodian_user_id">Cash Custodian (Staff)</Label>
                    <Input
                      type="select"
                      name="custodian_user_id"
                      id="custodian_user_id"
                      value={formik.values.custodian_user_id}
                      onChange={formik.handleChange}
                      disabled={userLoading}
                    >
                      <option value="">Not assigned</option>
                      {userOptions.map(item => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </Input>
                    <div className="small text-muted mt-1">
                      Staff member who physically holds this cash-in-hand account.
                    </div>
                  </div>
                </Col>
              </Row>
            )}
          </CardBody>
        </Card>

        <Card className="border mt-3">
          <CardBody>
            <h5 className="mb-3">Financial Setup</h5>
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <Label htmlFor="opening_balance">Opening Balance</Label>
                  <InputField
                    type="number"
                    name="opening_balance"
                    id="opening_balance"
                    min="0"
                    value={formik.values.opening_balance}
                    onChange={formik.handleChange}
                    invalid={
                      formik.touched.opening_balance && !!formik.errors.opening_balance
                    }
                  />
                  {formik.touched.opening_balance && formik.errors.opening_balance && (
                    <FormFeedback type="invalid">
                      {formik.errors.opening_balance}
                    </FormFeedback>
                  )}
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <div className="text-end mt-3">
          <Button color="success" type="submit" disabled={isSubmitDisabled}>
            {buttonText}
          </Button>
        </div>
      </Form>
    </ThemeModal>
  )
}

export default AccountAddEdit
